import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../utils/api';

type BroadcastStatus = 'DRAFT' | 'SCHEDULED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
type AudienceType = 'PLAYERS' | 'GROUPS' | 'MIXED';
type ParseMode = 'HTML' | 'Markdown' | 'NONE';

type BroadcastItem = {
  id: string;
  title: string;
  status: BroadcastStatus;
  messageText: string;
  parseMode: ParseMode;
  audienceType: AudienceType;
  audienceFilterJson: string | null;
  deliverySettingsJson: string | null;
  scheduledAt: string | null;
  recipientsPlanned: number;
  recipientsSent: number;
  recipientsFailed: number;
  recipientsSkipped: number;
  createdAt: string;
  updatedAt: string;
};

type TemplateItem = {
  id: string;
  name: string;
  messageText: string;
  parseMode: ParseMode;
};

type PreviewResponse = {
  total: number;
  players: number;
  groups: number;
  sample: Array<{
    targetType: string;
    playerId: string | null;
    chatId: string;
    threadId: string | null;
  }>;
};

type PlayerOption = {
  id: string;
  name: string;
  telegramChatId: string | null;
  telegramUsername: string | null;
  singlesLevel: string | null;
  doublesLevel: string | null;
};

type TournamentOption = {
  id: string;
  name: string;
  status: string;
};

const defaultFilters = {
  players: {
    isActive: true,
    telegramLanguage: [] as Array<'RO' | 'RU'>,
    singlesLevels: [] as string[],
    doublesLevels: [] as string[],
    tournamentId: '',
    excludeTournamentId: '',
    includePlayerIds: [] as string[],
    excludePlayerIds: [] as string[]
  },
  groups: {
    chatIds: [] as string[],
    chatTopicPairs: [] as Array<{ chatId: string; threadId?: string }>
  }
};

const defaultDelivery = {
  maxRecipientsPerRun: 500,
  messagesPerBatch: 25,
  pauseBetweenMessagesMs: 350,
  pauseBetweenBatchesMs: 2500,
  maxRetries: 2,
  retryDelayMs: 2000,
  disableWebPagePreview: false
};

const safeJsonParse = <T,>(value: string | null | undefined, fallback: T): T => {
  try {
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return (parsed && typeof parsed === 'object') ? { ...fallback, ...parsed } : fallback;
  } catch {
    return fallback;
  }
};

const TelegramBroadcasts: React.FC = () => {
  const [items, setItems] = useState<BroadcastItem[]>([]);
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);

  const [title, setTitle] = useState('');
  const [messageText, setMessageText] = useState('');
  const [parseMode, setParseMode] = useState<ParseMode>('HTML');
  const [audienceType, setAudienceType] = useState<AudienceType>('PLAYERS');
  const [playerLanguages, setPlayerLanguages] = useState('');
  const [singlesLevels, setSinglesLevels] = useState<string[]>([]);
  const [doublesLevels, setDoublesLevels] = useState<string[]>([]);
  const [tournamentId, setTournamentId] = useState('');
  const [excludeTournamentId, setExcludeTournamentId] = useState('');
  const [includePlayerIds, setIncludePlayerIds] = useState<string[]>([]);
  const [excludePlayerIds, setExcludePlayerIds] = useState<string[]>([]);
  const [groupTargets, setGroupTargets] = useState('');
  const [playerOptions, setPlayerOptions] = useState<PlayerOption[]>([]);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [tournamentOptions, setTournamentOptions] = useState<TournamentOption[]>([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);
  const [maxRecipients, setMaxRecipients] = useState(defaultDelivery.maxRecipientsPerRun);
  const [messagesPerBatch, setMessagesPerBatch] = useState(defaultDelivery.messagesPerBatch);
  const [pauseBetweenMessagesMs, setPauseBetweenMessagesMs] = useState(defaultDelivery.pauseBetweenMessagesMs);
  const [pauseBetweenBatchesMs, setPauseBetweenBatchesMs] = useState(defaultDelivery.pauseBetweenBatchesMs);
  const [maxRetries, setMaxRetries] = useState(defaultDelivery.maxRetries);
  const [retryDelayMs, setRetryDelayMs] = useState(defaultDelivery.retryDelayMs);
  const [disableWebPagePreview, setDisableWebPagePreview] = useState(defaultDelivery.disableWebPagePreview);

  const [templateName, setTemplateName] = useState('');
  const [templateSaving, setTemplateSaving] = useState(false);
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  const selectedItem = useMemo(() => items.find((item) => item.id === selectedId) || null, [items, selectedId]);

  const buildPayload = useCallback(() => {
    const languageValues = playerLanguages
      .split(',')
      .map((value) => value.trim().toUpperCase())
      .filter((value) => value === 'RO' || value === 'RU') as Array<'RO' | 'RU'>;
    const singlesValues = Array.from(new Set(singlesLevels.map((value) => value.trim()).filter(Boolean)));
    const doublesValues = Array.from(new Set(doublesLevels.map((value) => value.trim()).filter(Boolean)));
    const includeIds = Array.from(new Set(includePlayerIds.map((value) => value.trim()).filter(Boolean)));
    const excludeIds = Array.from(new Set(excludePlayerIds.map((value) => value.trim()).filter(Boolean)));

    const parsedGroupTargets = groupTargets
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [chatIdRaw, threadIdRaw] = line.split(':').map((chunk) => chunk.trim());
        return {
          chatId: chatIdRaw,
          threadId: threadIdRaw || undefined
        };
      })
      .filter((item) => item.chatId);

    return {
      title: title.trim(),
      messageText: messageText.trim(),
      parseMode,
      audienceType,
      audienceFilters: {
        ...defaultFilters,
        players: {
          ...defaultFilters.players,
          isActive: true,
          telegramLanguage: languageValues,
          singlesLevels: singlesValues,
          doublesLevels: doublesValues,
          tournamentId: tournamentId.trim() || null,
          excludeTournamentId: excludeTournamentId.trim() || null,
          includePlayerIds: includeIds,
          excludePlayerIds: excludeIds
        },
        groups: {
          chatIds: parsedGroupTargets.filter((item) => !item.threadId).map((item) => item.chatId),
          chatTopicPairs: parsedGroupTargets.filter((item) => Boolean(item.threadId))
        }
      },
      deliverySettings: {
        maxRecipientsPerRun: Number(maxRecipients) || defaultDelivery.maxRecipientsPerRun,
        messagesPerBatch: Number(messagesPerBatch) || defaultDelivery.messagesPerBatch,
        pauseBetweenMessagesMs: Number(pauseBetweenMessagesMs) || defaultDelivery.pauseBetweenMessagesMs,
        pauseBetweenBatchesMs: Number(pauseBetweenBatchesMs) || defaultDelivery.pauseBetweenBatchesMs,
        maxRetries: Number(maxRetries) || defaultDelivery.maxRetries,
        retryDelayMs: Number(retryDelayMs) || defaultDelivery.retryDelayMs,
        disableWebPagePreview
      }
    };
  }, [
    title,
    messageText,
    parseMode,
    audienceType,
    playerLanguages,
    singlesLevels,
    doublesLevels,
    tournamentId,
    excludeTournamentId,
    includePlayerIds,
    excludePlayerIds,
    groupTargets,
    maxRecipients,
    messagesPerBatch,
    pauseBetweenMessagesMs,
    pauseBetweenBatchesMs,
    maxRetries,
    retryDelayMs,
    disableWebPagePreview
  ]);

  const insertTextAtCursor = (before: string, after: string = '') => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = messageText.substring(start, end);
    const newText = messageText.substring(0, start) + before + selectedText + after + messageText.substring(end);
    setMessageText(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatBold = () => insertTextAtCursor('<b>', '</b>');
  const formatItalic = () => insertTextAtCursor('<i>', '</i>');
  const formatUnderline = () => insertTextAtCursor('<u>', '</u>');
  const formatStrikethrough = () => insertTextAtCursor('<s>', '</s>');
  const formatCode = () => insertTextAtCursor('<code>', '</code>');
  const formatPre = () => insertTextAtCursor('<pre>', '</pre>');
  const formatBlockquote = () => insertTextAtCursor('<blockquote>', '</blockquote>');
  const formatSpoiler = () => insertTextAtCursor('<span class="tg-spoiler">', '</span>');
  const formatLink = () => {
    const textarea = messageTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = messageText.substring(start, end) || 'текст ссылки';
    const url = window.prompt('Введите URL', 'https://') || 'https://';
    const before = `<a href="${url}">`;
    const after = '</a>';
    const newText = messageText.substring(0, start) + before + selectedText + after + messageText.substring(end);
    setMessageText(newText);
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const applyItemToForm = useCallback((item: BroadcastItem) => {
    setTitle(item.title || '');
    setMessageText(item.messageText || '');
    setParseMode((item.parseMode || 'HTML') as ParseMode);
    setAudienceType((item.audienceType || 'PLAYERS') as AudienceType);

    const filters = safeJsonParse(item.audienceFilterJson, defaultFilters);
    setPlayerLanguages((filters.players?.telegramLanguage || []).join(', '));
    setSinglesLevels((filters.players?.singlesLevels || []).map((value: string) => String(value || '').trim()).filter(Boolean));
    setDoublesLevels((filters.players?.doublesLevels || []).map((value: string) => String(value || '').trim()).filter(Boolean));
    setTournamentId(String(filters.players?.tournamentId || ''));
    setExcludeTournamentId(String(filters.players?.excludeTournamentId || ''));
    setIncludePlayerIds((filters.players?.includePlayerIds || []).map((value: string) => String(value || '').trim()).filter(Boolean));
    setExcludePlayerIds((filters.players?.excludePlayerIds || []).map((value: string) => String(value || '').trim()).filter(Boolean));

    const groupRows: string[] = [];
    for (const chatId of filters.groups?.chatIds || []) groupRows.push(chatId);
    for (const pair of filters.groups?.chatTopicPairs || []) {
      groupRows.push(`${pair.chatId}:${pair.threadId || ''}`);
    }
    setGroupTargets(groupRows.join('\n'));

    const delivery = safeJsonParse(item.deliverySettingsJson, defaultDelivery);
    setMaxRecipients(Number(delivery.maxRecipientsPerRun) || defaultDelivery.maxRecipientsPerRun);
    setMessagesPerBatch(Number(delivery.messagesPerBatch) || defaultDelivery.messagesPerBatch);
    setPauseBetweenMessagesMs(Number(delivery.pauseBetweenMessagesMs) || defaultDelivery.pauseBetweenMessagesMs);
    setPauseBetweenBatchesMs(Number(delivery.pauseBetweenBatchesMs) || defaultDelivery.pauseBetweenBatchesMs);
    setMaxRetries(Number(delivery.maxRetries) || defaultDelivery.maxRetries);
    setRetryDelayMs(Number(delivery.retryDelayMs) || defaultDelivery.retryDelayMs);
    setDisableWebPagePreview(Boolean(delivery.disableWebPagePreview));
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<{ items: BroadcastItem[] }>('/telegram-broadcasts');
      setItems(response.items || []);
    } catch (error) {
      console.error('Ошибка загрузки рассылок:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await apiGet<{ items: TemplateItem[] }>('/telegram-broadcasts/templates');
      setTemplates(response.items || []);
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
    }
  }, []);

  const fetchPlayers = useCallback(async () => {
    setPlayersLoading(true);
    try {
      const data = await apiGet<
        { players?: Array<{ id: string; name: string; telegramChatId?: string | null; telegramUsername?: string | null; singlesLevel?: string | null; doublesLevel?: string | null }>; items?: Array<{ id: string; name: string; telegramChatId?: string | null; telegramUsername?: string | null; singlesLevel?: string | null; doublesLevel?: string | null }> } |
        Array<{ id: string; name: string; telegramChatId?: string | null; telegramUsername?: string | null; singlesLevel?: string | null; doublesLevel?: string | null }>
      >('/players?isActive=true&limit=2000');

      const source = Array.isArray(data)
        ? data
        : Array.isArray(data.players)
          ? data.players
          : Array.isArray(data.items)
            ? data.items
            : [];

      const normalized = source
        .map((item) => ({
          id: String(item.id || '').trim(),
          name: String(item.name || '').trim(),
          telegramChatId: item.telegramChatId ? String(item.telegramChatId) : null,
          telegramUsername: item.telegramUsername ? String(item.telegramUsername) : null,
          singlesLevel: item.singlesLevel ? String(item.singlesLevel).trim() : null,
          doublesLevel: item.doublesLevel ? String(item.doublesLevel).trim() : null
        }))
        .filter((item) => item.id && item.name)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

      setPlayerOptions(normalized);
    } catch (error) {
      console.error('Ошибка загрузки списка игроков:', error);
    } finally {
      setPlayersLoading(false);
    }
  }, []);

  const fetchTournaments = useCallback(async () => {
    setTournamentsLoading(true);
    try {
      const data = await apiGet<
        { tournaments?: Array<{ id: string; name: string; status?: string }>; items?: Array<{ id: string; name: string; status?: string }> } |
        Array<{ id: string; name: string; status?: string }>
      >('/tournaments?limit=2000');

      const source = Array.isArray(data)
        ? data
        : Array.isArray(data.tournaments)
          ? data.tournaments
          : Array.isArray(data.items)
            ? data.items
            : [];

      const normalized = source
        .map((item) => ({
          id: String(item.id || '').trim(),
          name: String(item.name || '').trim(),
          status: String(item.status || '').trim().toUpperCase()
        }))
        .filter((item) => item.id && item.name)
        .sort((a, b) => {
          const aActive = a.status === 'ACTIVE' ? 0 : 1;
          const bActive = b.status === 'ACTIVE' ? 0 : 1;
          if (aActive !== bActive) return aActive - bActive;
          return a.name.localeCompare(b.name, 'ru');
        });

      setTournamentOptions(normalized);
    } catch (error) {
      console.error('Ошибка загрузки списка турниров:', error);
    } finally {
      setTournamentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchTemplates();
    fetchPlayers();
    fetchTournaments();
  }, [fetchItems, fetchTemplates, fetchPlayers, fetchTournaments]);

  useEffect(() => {
    if (!items.some((item) => item.status === 'RUNNING')) return;
    const timer = window.setInterval(() => {
      fetchItems();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items, fetchItems]);

  const handlePlayersSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const selectedValues = Array.from(event.target.selectedOptions).map((option) => option.value);
    setter(selectedValues);
  };

  const handleCreate = async () => {
    const payload = buildPayload();
    if (!payload.title || !payload.messageText) {
      alert('Заполните заголовок и текст сообщения');
      return;
    }
    setSaving(true);
    try {
      const created = await apiPost<BroadcastItem>('/telegram-broadcasts', payload);
      setSelectedId(created.id);
      await fetchItems();
      setPreview(null);
      alert('Черновик рассылки создан');
    } catch (error: any) {
      alert(error?.message || 'Не удалось создать рассылку');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedId) {
      alert('Выберите рассылку из журнала');
      return;
    }
    const payload = buildPayload();
    if (!payload.title || !payload.messageText) {
      alert('Заполните заголовок и текст сообщения');
      return;
    }
    setSaving(true);
    try {
      await apiPut(`/telegram-broadcasts/${selectedId}`, payload);
      await fetchItems();
      setPreview(null);
      alert('Рассылка обновлена');
    } catch (error: any) {
      alert(error?.message || 'Не удалось обновить рассылку');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedId) {
      alert('Сначала сохраните или выберите рассылку');
      return;
    }
    setPreviewLoading(true);
    try {
      const result = await apiPost<PreviewResponse>(`/telegram-broadcasts/${selectedId}/preview`, {});
      setPreview(result);
    } catch (error: any) {
      alert(error?.message || 'Не удалось выполнить preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendNow = async () => {
    if (!selectedId) {
      alert('Сначала сохраните или выберите рассылку');
      return;
    }
    const confirmed = window.confirm('Запустить рассылку сейчас?');
    if (!confirmed) return;
    setSending(true);
    try {
      await apiPost(`/telegram-broadcasts/${selectedId}/send`, {});
      await fetchItems();
      alert('Рассылка запущена в фоне. Статус обновится в журнале.');
    } catch (error: any) {
      alert(error?.message || 'Не удалось запустить рассылку');
    } finally {
      setSending(false);
    }
  };

  const handleCancel = async (id: string) => {
    const confirmed = window.confirm('Отменить рассылку?');
    if (!confirmed) return;
    try {
      await apiPost(`/telegram-broadcasts/${id}/cancel`, {});
      await fetchItems();
    } catch (error: any) {
      alert(error?.message || 'Не удалось отменить рассылку');
    }
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim();
    if (!name || !messageText.trim()) {
      alert('Укажите название шаблона и текст сообщения');
      return;
    }
    setTemplateSaving(true);
    try {
      await apiPost('/telegram-broadcasts/templates', {
        name,
        messageText: messageText.trim(),
        parseMode
      });
      setTemplateName('');
      await fetchTemplates();
      alert('Шаблон сохранен');
    } catch (error: any) {
      alert(error?.message || 'Не удалось сохранить шаблон');
    } finally {
      setTemplateSaving(false);
    }
  };

  const getStatusBadge = (status: BroadcastStatus) => {
    if (status === 'DRAFT') return 'bg-gray-100 text-gray-800';
    if (status === 'SCHEDULED') return 'bg-yellow-100 text-yellow-800';
    if (status === 'RUNNING') return 'bg-blue-100 text-blue-800';
    if (status === 'COMPLETED') return 'bg-green-100 text-green-800';
    if (status === 'FAILED') return 'bg-red-100 text-red-800';
    return 'bg-gray-200 text-gray-800';
  };

  const playerNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of playerOptions) {
      const telegramSuffix = option.telegramUsername ? ` • @${option.telegramUsername.replace(/^@/, '')}` : '';
      map.set(option.id, `${option.name}${telegramSuffix}`);
    }
    return map;
  }, [playerOptions]);

  const singlesLevelOptions = useMemo(() => {
    return Array.from(
      new Set(
        playerOptions
          .map((player) => String(player.singlesLevel || '').trim())
          .filter((value) => value && value.toUpperCase() !== 'UNKNOWN')
      )
    ).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [playerOptions]);

  const doublesLevelOptions = useMemo(() => {
    return Array.from(
      new Set(
        playerOptions
          .map((player) => String(player.doublesLevel || '').trim())
          .filter((value) => value && value.toUpperCase() !== 'UNKNOWN')
      )
    ).sort((a, b) => a.localeCompare(b, 'ru'));
  }, [playerOptions]);

  const handleLevelSelectChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const selectedValues = Array.from(event.target.selectedOptions).map((option) => option.value);
    setter(selectedValues);
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Рассылки Telegram</h1>
        <p className="text-sm text-gray-600">MVP: создание, preview аудитории, запуск сейчас, шаблоны и журнал.</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Заголовок</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Например: Анонс турнира выходного дня"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Parse mode</label>
            <select value={parseMode} onChange={(e) => setParseMode(e.target.value as ParseMode)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="HTML">HTML</option>
              <option value="Markdown">Markdown</option>
              <option value="NONE">NONE</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Текст сообщения</label>
          <div className="flex flex-wrap gap-1 mb-2">
            <button type="button" onClick={formatBold} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 font-bold" title="Жирный">B</button>
            <button type="button" onClick={formatItalic} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 italic" title="Курсив">I</button>
            <button type="button" onClick={formatUnderline} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 underline" title="Подчеркнутый">U</button>
            <button type="button" onClick={formatStrikethrough} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 line-through" title="Зачеркнутый">S</button>
            <button type="button" onClick={formatCode} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 font-mono" title="Код">{"</>"}</button>
            <button type="button" onClick={formatPre} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 font-mono" title="Блок кода">PRE</button>
            <button type="button" onClick={formatLink} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50" title="Ссылка">🔗</button>
            <button type="button" onClick={formatBlockquote} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50" title="Цитата">❝</button>
            <button type="button" onClick={formatSpoiler} className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50" title="Спойлер">🙈</button>
          </div>
          <textarea
            ref={messageTextareaRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[140px]"
            placeholder="Текст рассылки..."
          />
          <div className="mt-1 text-xs text-gray-500">
            Форматирование вставляет HTML-теги Telegram (работает как редактор Telegram в режиме HTML).
          </div>
          <label className="mt-2 inline-flex items-center gap-2 text-xs text-gray-700 select-none">
            <input
              type="checkbox"
              checked={disableWebPagePreview}
              onChange={(e) => setDisableWebPagePreview(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Отключить превью ссылок (без карточек URL)
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Аудитория</label>
            <select value={audienceType} onChange={(e) => setAudienceType(e.target.value as AudienceType)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="PLAYERS">Только игроки</option>
              <option value="GROUPS">Только группы/топики</option>
              <option value="MIXED">Игроки + группы/топики</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Языки игроков</label>
            <input value={playerLanguages} onChange={(e) => setPlayerLanguages(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="RO, RU" />
          </div>
          <div className="md:col-span-2 text-xs text-gray-500 flex items-end">
            Фильтр по уровням: выбор из уровней активных игроков.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1 gap-2">
              <label className="block text-xs font-medium text-gray-700">Singles уровни</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSinglesLevels(singlesLevelOptions)}
                  disabled={singlesLevelOptions.length === 0}
                  className="text-[11px] text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  onClick={() => setSinglesLevels([])}
                  disabled={singlesLevels.length === 0}
                  className="text-[11px] text-gray-700 hover:text-gray-900 disabled:text-gray-400"
                >
                  Снять выбор
                </button>
                <span className="text-xs text-gray-500">{singlesLevels.length} выбрано</span>
              </div>
            </div>
            <select
              multiple
              value={singlesLevels}
              onChange={(e) => handleLevelSelectChange(e, setSinglesLevels)}
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm min-h-[120px]"
            >
              {singlesLevelOptions.map((level) => (
                <option key={`single_${level}`} value={level}>{level}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1 gap-2">
              <label className="block text-xs font-medium text-gray-700">Doubles уровни</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDoublesLevels(doublesLevelOptions)}
                  disabled={doublesLevelOptions.length === 0}
                  className="text-[11px] text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                >
                  Выбрать все
                </button>
                <button
                  type="button"
                  onClick={() => setDoublesLevels([])}
                  disabled={doublesLevels.length === 0}
                  className="text-[11px] text-gray-700 hover:text-gray-900 disabled:text-gray-400"
                >
                  Снять выбор
                </button>
                <span className="text-xs text-gray-500">{doublesLevels.length} выбрано</span>
              </div>
            </div>
            <select
              multiple
              value={doublesLevels}
              onChange={(e) => handleLevelSelectChange(e, setDoublesLevels)}
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm min-h-[120px]"
            >
              {doublesLevelOptions.map((level) => (
                <option key={`double_${level}`} value={level}>{level}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Только зарегистрированные в турнире</label>
            <select
              value={tournamentId}
              onChange={(e) => setTournamentId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Не выбрано</option>
              {tournamentOptions.map((item) => (
                <option key={`inc_t_${item.id}`} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Исключить зарегистрированных в турнире</label>
            <select
              value={excludeTournamentId}
              onChange={(e) => setExcludeTournamentId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Не выбрано</option>
              {tournamentOptions.map((item) => (
                <option key={`exc_t_${item.id}`} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
          <div className="text-xs text-gray-500 flex items-end">
            {playersLoading
              ? 'Загрузка игроков...'
              : tournamentsLoading
                ? 'Загрузка турниров...'
                : `Доступно для выбора: ${playerOptions.length} активных игроков • ${tournamentOptions.length} турниров`}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1 gap-2">
              <label className="block text-xs font-medium text-gray-700">Включить игроков</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIncludePlayerIds(playerOptions.map((player) => player.id))}
                  disabled={playerOptions.length === 0}
                  className="text-[11px] text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                >
                  Выбрать всех
                </button>
                <button
                  type="button"
                  onClick={() => setIncludePlayerIds([])}
                  disabled={includePlayerIds.length === 0}
                  className="text-[11px] text-gray-700 hover:text-gray-900 disabled:text-gray-400"
                >
                  Снять выбор
                </button>
                <span className="text-xs text-gray-500">{includePlayerIds.length} выбрано</span>
              </div>
            </div>
            <select
              multiple
              value={includePlayerIds}
              onChange={(e) => handlePlayersSelectChange(e, setIncludePlayerIds)}
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm min-h-[170px]"
            >
              {playerOptions.map((player) => (
                <option key={player.id} value={player.id}>
                  {playerNameById.get(player.id) || player.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1 gap-2">
              <label className="block text-xs font-medium text-gray-700">Исключить игроков</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setExcludePlayerIds(playerOptions.map((player) => player.id))}
                  disabled={playerOptions.length === 0}
                  className="text-[11px] text-blue-700 hover:text-blue-900 disabled:text-gray-400"
                >
                  Выбрать всех
                </button>
                <button
                  type="button"
                  onClick={() => setExcludePlayerIds([])}
                  disabled={excludePlayerIds.length === 0}
                  className="text-[11px] text-gray-700 hover:text-gray-900 disabled:text-gray-400"
                >
                  Снять выбор
                </button>
                <span className="text-xs text-gray-500">{excludePlayerIds.length} выбрано</span>
              </div>
            </div>
            <select
              multiple
              value={excludePlayerIds}
              onChange={(e) => handlePlayersSelectChange(e, setExcludePlayerIds)}
              className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm min-h-[170px]"
            >
              {playerOptions.map((player) => (
                <option key={player.id} value={player.id}>
                  {playerNameById.get(player.id) || player.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {(includePlayerIds.length > 0 || excludePlayerIds.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="border border-gray-200 rounded-lg p-2">
              <div className="font-medium text-gray-700 mb-1">Включены</div>
              <div className="flex flex-wrap gap-1">
                {includePlayerIds.map((id) => (
                  <span key={`inc_${id}`} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                    {playerNameById.get(id) || id}
                  </span>
                ))}
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg p-2">
              <div className="font-medium text-gray-700 mb-1">Исключены</div>
              <div className="flex flex-wrap gap-1">
                {excludePlayerIds.map((id) => (
                  <span key={`exc_${id}`} className="px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                    {playerNameById.get(id) || id}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-gray-500">
          Подсказка: удерживайте `Ctrl`/`Cmd` для множественного выбора.
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Группы/топики (по строкам: `chatId` или `chatId:threadId`)</label>
          <textarea value={groupTargets} onChange={(e) => setGroupTargets(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[90px]" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Лимит за запуск</label>
            <input type="number" value={maxRecipients} onChange={(e) => setMaxRecipients(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="max/run" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Размер батча</label>
            <input type="number" value={messagesPerBatch} onChange={(e) => setMessagesPerBatch(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="batch size" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Пауза между сообщениями (мс)</label>
            <input type="number" value={pauseBetweenMessagesMs} onChange={(e) => setPauseBetweenMessagesMs(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="pause msg ms" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Пауза между батчами (мс)</label>
            <input type="number" value={pauseBetweenBatchesMs} onChange={(e) => setPauseBetweenBatchesMs(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="pause batch ms" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Макс. повторов</label>
            <input type="number" value={maxRetries} onChange={(e) => setMaxRetries(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="retries" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Задержка повтора (мс)</label>
            <input type="number" value={retryDelayMs} onChange={(e) => setRetryDelayMs(Number(e.target.value))} className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm" placeholder="retry delay ms" />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button onClick={handleCreate} disabled={saving} className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Сохраняем...' : 'Создать черновик'}
          </button>
          <button onClick={handleUpdate} disabled={saving || !selectedId} className="px-3 py-2 rounded-lg bg-gray-700 text-white text-sm hover:bg-gray-800 disabled:opacity-50">
            {saving ? 'Сохраняем...' : 'Обновить'}
          </button>
          <button onClick={handlePreview} disabled={previewLoading || !selectedId} className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">
            {previewLoading ? 'Preview...' : 'Preview аудитории'}
          </button>
          <button onClick={handleSendNow} disabled={sending || !selectedId} className="px-3 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50">
            {sending ? 'Отправляем...' : 'Отправить сейчас'}
          </button>
        </div>

        <div className="border-t pt-3">
          <div className="text-xs font-medium text-gray-700 mb-2">Шаблоны</div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[240px]" placeholder="Название шаблона" />
            <button onClick={handleSaveTemplate} disabled={templateSaving} className="px-3 py-2 rounded-lg bg-purple-600 text-white text-sm hover:bg-purple-700 disabled:opacity-50">
              {templateSaving ? 'Сохраняем...' : 'Сохранить шаблон'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setMessageText(item.messageText || '');
                  setParseMode(item.parseMode || 'HTML');
                }}
                className="px-2 py-1 rounded bg-purple-50 text-purple-700 text-xs hover:bg-purple-100"
                title={item.messageText}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm font-semibold text-gray-900 mb-2">Preview</div>
          <div className="text-sm text-gray-700">Всего: {preview.total} • Игроки: {preview.players} • Группы/топики: {preview.groups}</div>
          <div className="mt-2 max-h-40 overflow-auto border border-gray-100 rounded p-2 text-xs text-gray-600">
            {preview.sample.map((row, idx) => (
              <div key={`${row.chatId}_${row.threadId || ''}_${idx}`}>
                {row.targetType} • {row.chatId}{row.threadId ? `:${row.threadId}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Журнал рассылок</h2>
          <button onClick={fetchItems} className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50">Обновить</button>
        </div>
        {loading ? (
          <div className="p-4 text-sm text-gray-500">Загрузка...</div>
        ) : items.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">Пока нет рассылок</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-3 py-2">Статус</th>
                  <th className="text-left px-3 py-2">Заголовок</th>
                  <th className="text-left px-3 py-2">Аудитория</th>
                  <th className="text-left px-3 py-2">Счетчики</th>
                  <th className="text-left px-3 py-2">Создано</th>
                  <th className="text-left px-3 py-2">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className={selectedId === item.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadge(item.status)}`}>{item.status}</span>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => {
                          setSelectedId(item.id);
                          applyItemToForm(item);
                          setPreview(null);
                        }}
                        className="text-left text-blue-700 hover:text-blue-900"
                      >
                        {item.title}
                      </button>
                    </td>
                    <td className="px-3 py-2">{item.audienceType}</td>
                    <td className="px-3 py-2 text-xs text-gray-700">
                      plan:{item.recipientsPlanned} / ok:{item.recipientsSent} / fail:{item.recipientsFailed} / skip:{item.recipientsSkipped}
                    </td>
                    <td className="px-3 py-2 text-xs">{new Date(item.createdAt).toLocaleString('ru-RU')}</td>
                    <td className="px-3 py-2">
                      {['DRAFT', 'SCHEDULED', 'RUNNING'].includes(item.status) && (
                        <button onClick={() => handleCancel(item.id)} className="text-red-600 text-xs hover:text-red-700">Отменить</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramBroadcasts;
